// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { AccordionSectionPanel } from "@insite/mobius/AccordionSection";
import { AccordionSectionHeaderProps } from "@insite/mobius/AccordionSection/AccordionSectionHeader";
import AccordionSectionPresentationPropsDefault from "@insite/mobius/AccordionSection/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import { IconPositioner } from "@steiner/MobiusOverrides/AccordionSection/AccordionSection";
import Filter from "@steiner/XDL/Icons/Filter";
import Minus from "@steiner/XDL/Icons/Minus";
import Plus from "@steiner/XDL/Icons/Plus";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import { xBaseTheme } from "@steiner/XDL/types";
import { color } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

export const plpAccordion = {
    mergeCss: true,
    headerProps: {
        css: css`
            @media print {
                & > button {
                    display: flex !important;
                }
            }

            & > button {
                &,
                &:focus {
                    background-color: ${color("common.ui8")};
                    padding: 12px;
                }
            }

            ${/* sc-selector */ AccordionSectionPanel} + & button {
                &:focus {
                    background-color: ${color("common.ui8")};
                    padding: 12px;
                }
            }
        `,
    },
    panelProps: {
        css: css`
            border-color: ${color("common.ui3")};
            padding: 12px;

            & > div {
                font-size: 14px;
                font-weight: normal;
            }
        `,
    },
    titleTypographyProps: {
        size: "20px",
        weight: fontConfig.weight.bold,
        fontFamily: fontConfig.headingFontFamily,
        lineHeight: "24px",
        color: "common.white",
        css: css`
            margin: 0;
        `,
    },
    toggleIconProps: {
        color: "common.white",
        src: Filter,
        size: 25,
        css: css`
            &,
            & > svg {
                height: 24px;
            }
        `,
    },
    expandIconProps: {
        color: "common.white",
        src: Filter,
        size: 25,
        css: css`
            &,
            & > svg {
                height: 24px;
            }
        `,
    },
    toggleTransition: {
        positionerCss: css`
            @media print {
                display: none;
            }
        `,
    },
} as xBaseTheme["accordion"]["sectionDefaultProps"];

export default {
    defaultProps: {
        mergeCss: true,
    },
    sectionDefaultProps: {
        ...AccordionSectionPresentationPropsDefault,
        mergeCss: true,
        headerProps: {
            css: css`
                @media print {
                    & > button {
                        display: flex !important;
                    }
                }

                & > button {
                    &,
                    &:focus {
                        border: 1px solid ${color("common.border")};
                        border-bottom-width: 0;
                        background-color: ${color("common.white")};
                        padding: 8px;
                        &[data-stop="true"] {
                            ${IconWrapper}:nth-of-type(2), ${IconPositioner} {
                                display: none;
                            }
                        }
                    }
                }

                ${/* sc-selector */ AccordionSectionPanel} + & button {
                    &[aria-expanded="true"]:focus {
                        border-bottom-width: 0;
                    }
                    &:focus {
                        border: 1px solid ${color("common.border")};
                        border-top-width: 0;
                        background-color: ${color("common.white")};
                        padding: 8px;
                    }
                }

                ${({ expanded }: AccordionSectionHeaderProps) =>
                    !expanded &&
                    css`
                        & > button {
                            &,
                            &:focus {
                                border-bottom-width: 1px;
                            }
                        }
                    `}
            `,
        },
        panelProps: {
            css: css`
                border-color: ${color("common.ui3")};
                padding: 8px;

                & > div {
                    font-size: 14px;
                    font-weight: normal;
                }
            `,
        },
        titleTypographyProps: {
            variant: "body",
            forwardAs: "span",
            weight: "bold",
            color: "text.main",
            size: 12,
        },
        toggleIconProps: {
            color: "primary",
            src: Minus,
            size: 13,
        },
        expandIconProps: {
            color: "primary",
            src: Plus,
        },
        toggleTransition: {
            positionerCss: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
} as xBaseTheme["accordion"];
